<template>
  <div class="compu-minute-signatures">
    <div class="compu-minute-signatures__panel">
      <div
        v-for="(participant, index) in localValue"
        :key="getParticipantKey(participant, index)"
        class="compu-minute-signatures__row"
      >
        <div class="compu-minute-signatures__role">
          {{ getGroupName(participant) }}
        </div>

        <div class="compu-minute-signatures__field">
          <custom-drop-down
            :value="getSelectedPosition(participant)"
            label="Puesto:"
            :options="getPositionOptions(participant.groupId)"
            fieldkey="signaturePositionId"
            fieldname="signaturePositionName"
            placeholder="Seleccione"
            :disabled="readonly"
            @input="setParticipantPosition(participant, $event)"
          />
        </div>

        <div class="compu-minute-signatures__field">
          <custom-input
            v-model="participant.signature.name"
            label="Nombre:"
            type="text"
            placeholder=""
            :disabled="readonly"
          />
        </div>

        <div class="compu-minute-signatures__field">
          <custom-drop-down
            :value="getSelectedParticipation(participant)"
            label="Tipo de Participación:"
            :options="participationTypes"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :disabled="readonly"
            @input="setParticipantParticipation(participant, $event)"
          />
        </div>

        <button
          v-if="canRemoveParticipant(participant)"
          type="button"
          class="compu-minute-signatures__remove"
          title="Eliminar participante"
          @click="deleteParticipant(index)"
        >
          <i class="far fa-times-circle" />
        </button>
      </div>
    </div>

    <div v-if="!readonly" class="compu-minute-signatures__actions">
      <action-button
        variant="success"
        class="px-3"
        i="fas fa-file-plus"
        @click="addParticipant"
      >
        Añadir
      </action-button>
    </div>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';

export default {
  name: 'CompuMinuteSignatures',
  components: {
    ActionButton,
    CustomDropDown,
    CustomInput,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Array,
      default: () => [],
    },
    participationTypes: {
      type: Array,
      default: () => [],
    },
    groupNameOverrides: {
      type: Object,
      default: () => ({}),
    },
    allowedGroupList: {
      type: Array,
      default: () => [1, 4, 5, 15, 16],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localValue: this.cloneValue(this.value),
    };
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        if (JSON.stringify(value) !== JSON.stringify(this.localValue)) {
          this.localValue = this.cloneValue(value);
        }
      },
    },
    localValue: {
      deep: true,
      handler(value) {
        if (JSON.stringify(value) !== JSON.stringify(this.value)) {
          this.$emit('input', this.cloneValue(value));
        }
      },
    },
  },
  methods: {
    cloneValue(value) {
      return JSON.parse(JSON.stringify(value || []));
    },
    addParticipant() {
      if (this.readonly) {
        return;
      }

      this.localValue.push({
        id: this.getNextParticipantId(),
        signature: {
          name: '',
          positionId: null,
          participationId: null,
          alternateId: null,
        },
        groupId: 0,
        isRequired: false,
        isRemovable: true,
        isDisabled: false,
      });
    },
    deleteParticipant(index) {
      if (this.readonly) {
        return;
      }

      if (!this.canRemoveParticipant(this.localValue[index])) {
        return;
      }

      this.localValue.splice(index, 1);
    },
    canRemoveParticipant(participant) {
      if (this.readonly) {
        return false;
      }

      return (
        this.normalizeBoolean(participant?.isRemovable) === true ||
        this.getParticipantRemovableFlag(participant) === true
      );
    },
    getParticipantRemovableFlag(participant) {
      const configuredOption = this.getParticipantConfigurationOption(
        participant
      );

    return this.normalizeBoolean(
      participant?.isRemovableFlag ??
        participant?.IsRemovableFlag ??
        participant?.removableFlag ??
        participant?.RemovableFlag ??
        participant?.isAdditional ??
        participant?.IsAdditional ??
        participant?.value ??
        participant?.Value ??
        configuredOption?.isRemovableFlag ??
        configuredOption?.IsRemovableFlag ??
        configuredOption?.removableFlag ??
        configuredOption?.RemovableFlag ??
        configuredOption?.isAdditional ??
        configuredOption?.IsAdditional ??
        configuredOption?.value ??
        configuredOption?.Value
    );
  },
    getParticipantConfigurationOption(participant) {
      const groupId =
        participant?.groupId ??
        participant?.attendanceGroupId ??
        participant?.signatureGroupId ??
        participant?.signature?.positionId?.signatureGroupId ??
        0;

      return this.options.find((option) => {
        if (!this.hasRemovableFlag(option)) {
          return false;
        }

        const optionGroupId =
          option.signatureGroupId ??
          option.groupId ??
          option.attendanceGroupId ??
          option.signature?.positionId?.signatureGroupId ??
          option.id ??
          0;

        return Number(optionGroupId) === Number(groupId);
      });
    },
    hasRemovableFlag(value) {
      return (
      value?.isRemovableFlag !== undefined ||
      value?.IsRemovableFlag !== undefined ||
      value?.removableFlag !== undefined ||
      value?.RemovableFlag !== undefined ||
      value?.isAdditional !== undefined ||
      value?.IsAdditional !== undefined ||
      value?.value !== undefined ||
      value?.Value !== undefined
    );
  },
    normalizeBoolean(value) {
      if (value === undefined || value === null) {
        return null;
      }

      if (typeof value === 'boolean') {
        return value;
      }

      if (typeof value === 'number') {
        return value === 1;
      }

      if (typeof value === 'string') {
        const normalizedValue = value.trim().toLowerCase();

        if (['true', '1', 'yes', 'si', 's'].includes(normalizedValue)) {
          return true;
        }

        if (['false', '0', 'no', 'n'].includes(normalizedValue)) {
          return false;
        }
      }

      return null;
    },
    getNextParticipantId() {
      const ids = this.localValue
        .map((participant) => Number(participant.id))
        .filter((id) => Number.isFinite(id));

      return ids.length ? Math.min(...ids) - 1 : -1;
    },
    getParticipantKey(participant, index) {
      return participant.id ?? `participant-${index}`;
    },
    getSelectedPosition(participant) {
      const positionId = participant.signature?.positionId?.signaturePositionId;

      if (!positionId) {
        return null;
      }

      return (
        this.getPositionOptions(participant.groupId).find(
          (option) => option.signaturePositionId == positionId
        ) ?? null
      );
    },
    setParticipantPosition(participant, value) {
      participant.signature.positionId = value ?? null;
    },
    getSelectedParticipation(participant) {
      const participationId = participant.signature?.participationId?.id;

      if (!participationId) {
        return null;
      }

      return (
        this.participationTypes.find((option) => option.id == participationId) ??
        null
      );
    },
    setParticipantParticipation(participant, value) {
      participant.signature.participationId = value ?? null;
    },
    getPositionOptions(groupId) {
      if (groupId !== 0) {
        return this.options.filter(
          (option) => option.signatureGroupId == groupId
        );
      }

      const filteredOptions = this.options.filter((option) =>
        this.allowedGroupList.includes(option.signatureGroupId)
      );
      const defaultSignatures = this.options.filter(
        (option) =>
          option.signatureGroupId == 13 &&
          !filteredOptions.some(
            (group) => group.signaturePositionId == option.signaturePositionId
          )
      );

      return filteredOptions.concat(defaultSignatures);
    },
    getGroupName(participant) {
      const groupId =
        participant.groupId !== 0
          ? participant.groupId
          : participant.signature?.positionId?.signatureGroupId || 0;
      const signatureGroup = this.options.find(
        (option) => option.signatureGroupId == groupId
      );

      return (
        this.groupNameOverrides[groupId] ||
        signatureGroup?.signatureGroupName ||
        'Funcionario'
      );
    },
  },
};
</script>

<style scoped lang="scss">
.compu-minute-signatures {
  &__panel {
    background: #f4f6f3;
    padding: 0 1.25rem;
  }

  &__row {
    align-items: center;
    display: grid;
    gap: 1rem;
    grid-template-columns:
      minmax(180px, 1fr)
      repeat(3, minmax(180px, 1.15fr))
      auto;
    padding: 1rem 0;
  }

  &__row:not(:first-child) {
    border-top: 1px solid #e2e2e2;
  }

  &__role {
    color: #11291b;
    font-weight: 600;
    padding-top: 1.25rem;
  }

  &__field {
    min-width: 0;
  }

  &__remove {
    align-self: center;
    background: transparent;
    border: 0;
    color: #ff7058;
    cursor: pointer;
    font-size: 1rem;
    margin-top: 1.6rem;
    padding: 0.25rem;
  }

  &__actions {
    margin-top: 1rem;
    text-align: right;
  }
}

@media (max-width: 991px) {
  .compu-minute-signatures {
    &__row {
      grid-template-columns: 1fr;
    }

    &__role,
    &__remove {
      margin-top: 0;
      padding-top: 0;
    }
  }
}
</style>
